/* eslint-disable no-empty */
// src/embed/widget-pro-card.jsx
import React, { Component } from "react";
import { createRoot } from "react-dom/client";
import "./widget-styles.scss";
import ProCard from "../components/Lists/ProCard/ProCard";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const parse = (v) =>
  v == null ? undefined : v === "" ? true : v === "true" ? true : v === "false" ? false : v;

// Attributes that should always remain strings (no boolean coercion)
const STRING_ATTRS = new Set(["title_prefix", "title_font_size", "title_color"]);
const parseStr = (v) => (v == null ? undefined : v);

class ErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error("[pro-card] component error:", err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 12, border: "2px solid #f33", background: "#fee" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Widget crashed</div>
          <code style={{ fontSize: 12 }}>{String(this.state.error)}</code>
        </div>
      );
    }
    return this.props.children;
  }
}

function register(tagName, Comp, { attrToProp = {} } = {}) {
  if (customElements.get(tagName)) return;

  class ReactElement extends HTMLElement {
    static get observedAttributes() {
      return Object.keys(attrToProp).map((k) => k.toLowerCase());
    }

    _propsFromAttrs() {
      const props = {};
      for (const [attr, prop] of Object.entries(attrToProp)) {
        const v = this.getAttribute(attr) ?? this.getAttribute(attr.toLowerCase());
        props[prop] = STRING_ATTRS.has(attr) ? parseStr(v) : parse(v);
      }
      return props;
    }

    _render() {
      if (!this._root) return;
      const p = this._propsFromAttrs();

      const origin = detectToolsOrigin();
      const proxyUrl = `${origin}/.netlify/functions/api-proxy`;
      window.__UK_PROXY_URL__ = proxyUrl;

      const app = (
        <ErrorBoundary>
          <Comp {...p} proxyUrl={proxyUrl} />
        </ErrorBoundary>
      );

      this._root.render(app);
    }

    connectedCallback() {
      const m = document.createElement("div");
      this.appendChild(m);
      this._root = createRoot(m);
      this._render();
    }
    attributeChangedCallback() { this._render(); }
    disconnectedCallback() { this._root?.unmount(); this._root = null; }
  }

  customElements.define(tagName, ReactElement);
}

register("pro-card", ProCard, {
  attrToProp: {
    pro_id: "pro_id",
    qa_mode: "qa_mode",
    show_avatar: "show_avatar",
    show_badge: "show_badge",
    show_name: "show_name",
    show_business: "show_business",
    title_prefix: "title_prefix",
    title_font_size: "title_font_size",
    title_color: "title_color",
  },
});
